import Collection from "@arcgis/core/core/Collection";
import * as versionManagementAdapterUtils from "@arcgis/core/versionManagement/versionAdapters/utils.js";
import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { StoreState } from "../../store/rootReducer";
import { setActiveGuid, setVersionInfo, setVersionsSharedWithMe } from "../../store/versionSlice";
import { getMapView } from "../../utils/arcgisUtils";
import { reconcileFromDefault } from "../../utils/newVersioningUtils";
import {
    canEditVersion,
    getVersionsSharedWithMe,
    isDefaultVersion,
    isMyVersion,
} from "../../utils/versioningUtils";

import CloseIcon from "../../assets/close.svg?react";
import MagnifyingGlassIcon from "../../assets/header/magnifyingglass.svg?react";
import PlusIcon from "../../assets/versionControl/plusIcon.svg?react";
import RefreshIcon from "../../assets/versionControl/refresh.svg?react";
import SortIcon from "../../assets/versionControl/sort.svg?react";

import Modal from "react-modal";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import { toast } from "react-toastify";
import { LeftPanelTabs, RightPanelTabs } from "../../constants/Panels";
import { AppContext, JobContext, UserContext, VersionContext } from "../../Context";
import { FeatureLayerNFS } from "../../extensions/FeatureLayerNFS";
import { setLeftPanelActiveTab, setLoading, setRightPanelActiveTab } from "../../store/appSlice";
import { deobscureString, hasAccessToGroups } from "../../utils/helpers";
import Loader from "../genericComponents/Loader/Loader";
import NfsButton from "../genericComponents/NfsButton/NfsButton";
import NfsDropdown from "../genericComponents/NfsDropdown/NfsDropdown";
import AcceptVersionAccess from "./AcceptVersionAccess";
import CreateNewVersion from "./CreateNewVersion";
import "./VersionControl.css";
import { handleAddVersionClick, notifyReconcileStatus } from "./VersionControlUtils";
import VersionPanel from "./VersionPanel";
import { addMerkeplan, deleteMerkeplan, getMerkeplanVersionNames } from "../../utils/nfsUtils";
import { getSelf } from "../../utils/authenticate";
import { AppConfig } from "../../AppConfig";
import type { VersionInfo, VersionInfoExtended } from "@arcgis/core/versionManagement/support/jsonTypes";
import type { VersionAdapter } from "@arcgis/core/versionManagement/versionAdapters/types";
import type FeatureLayer from "@arcgis/core/layers/FeatureLayer";

export const SwitchToDefaultEventType = "version:switchToDefault";
export const VersionIsDefaultEventType = "version:isDefault";

const VersionControl = () => {
    const [createVersionModalOpen, setCreateVersionModalOpen] = useState<boolean>(false);
    const [acceptVersionModalOpen, setAcceptVersionModalOpen] = useState<boolean>(false);
    const [versionToAccept, setVersionToAccept] = useState<VersionInfoExtended | null>(null);
    const [versionSearchString, setVersionSearchString] = useState<string>("");
    const [versionSort, setVersionSort] = useState<"A-Å" | "Å-A" | "Nyeste" | "Eldste">("A-Å");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [merkeplanList, setMerkeplanList] = useState<string[]>([]);
    const [allowedToCreateVersion, setAllowedToCreateVersion] = useState<boolean>(false);

    const userContext = useContext(UserContext);
    const versionContext = useContext(VersionContext);
    const versionsSharedWithMe = useSelector((state: StoreState) => state.version.versionsSharedWithMe);
    const leftPanelActiveTab = useSelector((state: StoreState) => state.app.leftPanelActiveTab);
    const rightPanelActiveTab = useSelector((state: StoreState) => state.app.rightPanelActiveTab);
    const appContext = useContext(AppContext);
    const selectedFeature = appContext?.selectedFeature.value;

    const jobsContext = useContext(JobContext);

    const dispatch = useDispatch();

    const versionServiceRef = versionContext!.versionServiceRef.value;

    const [versionInfos, setVersionInfos] = useState<VersionInfoExtended[]>();

    const versionInfo = useSelector((state: StoreState) => state.version.versionInfo);

    const closePanel = () => {
        dispatch(setLeftPanelActiveTab(LeftPanelTabs.UNDEFINED));
    };

    const reloadVersions = async () => {
        setIsLoading(true);
        const tempVersionInfos = await versionServiceRef.current.getVersionInfos();
        const filteredVersionInfos = tempVersionInfos.filter((vi) => vi.access === "public");
        await versionServiceRef.current.load();

        // Make all API calls in parallel but handle failures
        const results = await Promise.allSettled(
            filteredVersionInfos.map((versionInfo) =>
                versionServiceRef.current.getVersionInfoExtended(versionInfo.versionIdentifier)
            )
        );

        const extendedVersionInfos = results
            .filter(
                (result): result is PromiseFulfilledResult<VersionInfoExtended> =>
                    result.status === "fulfilled"
            )
            .map((result) => result.value);

        const failures = results.filter((result) => result.status === "rejected");

        if (failures.length > 0) {
            toast.warning(`${failures.length} av ${filteredVersionInfos.length} versjoner kunne ikke lastes.`);
        }

        setVersionInfos(extendedVersionInfos);
        setMerkeplanList(await getMerkeplanVersionNames());
        setIsLoading(false);
    };

    const loadService = async () => {
        await versionServiceRef.current.load();
        await reloadVersions();
    };

    // Users tend to not reconcile their versions manually, so we pull the latest changes from
    // Planroot every time a version is selected. Done before the layers switch version so the
    // map loads the reconciled state. A failed reconcile does not block the version switch.
    const reconcileBeforeSelect = async (vi: VersionInfo) => {
        if (!vi.versionIdentifier?.guid || isDefaultVersion(vi, versionServiceRef.current)) {
            return;
        }
        const user = userContext?.user.value;
        if (!(await canEditVersion(vi, versionsSharedWithMe ?? [], user))) {
            return;
        }

        dispatch(setLoading(true));
        try {
            notifyReconcileStatus(await reconcileFromDefault(vi.versionIdentifier.guid));
        } finally {
            dispatch(setLoading(false));
        }
    };

    const handleSelectVersion = async (
        vi: VersionInfo,
        shouldClosePanel: boolean = true,
        bypassWarning: boolean = false
    ) => {
        const map = getMapView()?.map;
        if (!map) {
            return;
        }
        let layers = map?.allLayers.toArray() as FeatureLayer[];
        layers = layers?.filter((l) => l.type === "feature");
        if (!layers) {
            return;
        }

        if (
            !bypassWarning &&
            selectedFeature !== null &&
            selectedFeature !== undefined &&
            [
                RightPanelTabs.EDITOR,
                RightPanelTabs.LIGHT_CALCULATOR,
                RightPanelTabs.OVERETT,
                RightPanelTabs.SEA_CABLE,
                RightPanelTabs.SECTOR,
            ].includes(rightPanelActiveTab)
        ) {
            // Confirmation popup that informs the user that the selected feature will be closed.
            // If the user confirms, the selected feature will be closed and the version will be changed.
            // If the user cancels, the version will not be changed.
            if (
                !globalThis.confirm(
                    "Du har et åpent objekt. Hvis du endrer versjon vil dette objektet lukkes. Endringer som ikke er lagret vil gå tapt. Vil du fortsette?"
                )
            ) {
                return;
            }
            appContext?.selectedFeature.set(null);
            dispatch(setRightPanelActiveTab(RightPanelTabs.UNDEFINED));
        }

        if (!bypassWarning && jobsContext?.selectedJob.value && rightPanelActiveTab === RightPanelTabs.JOBS) {
            // Confirmation popup that informs the user that the selected job will be closed.
            // If the user confirms, the selected job will be closed and the version will be changed.
            // If the user cancels, the version will not be changed.
            if (
                !globalThis.confirm(
                    "Du har en åpen jobb. Hvis du endrer versjon vil denne jobben lukkes. Endringer som ikke er lagret vil gå tapt. Vil du fortsette?"
                )
            ) {
                return;
            }
            dispatch(setRightPanelActiveTab(RightPanelTabs.UNDEFINED));
            jobsContext?.selectedJob.set(null);
        }

        if (!bypassWarning && appContext?.efsSelectedFeatures && appContext?.efsSelectedFeatures.value.length > 0) {
            // Confirmation popup that informs the user that the selected EFS features will be closed.
            // If the user confirms, the selected EFS features will be closed and the version will be changed.
            // If the user cancels, the version will not be changed.
            if (
                !globalThis.confirm(
                    "Du har åpne EFS-meldinger. Hvis du endrer versjon vil disse meldingene lukkes. Endringer som ikke er lagret vil gå tapt. Vil du fortsette?"
                )
            ) {
                return;
            }
            appContext?.efsSelectedFeatures.clear();
        }

        appContext?.selectedFeature.set(null);
        appContext?.shownFeature.set(null);

        await reconcileBeforeSelect(vi);

        let tables = map?.allTables.toArray() as FeatureLayer[];
        tables = tables?.filter((l) => l.type === "feature");
        layers = layers.concat(tables);
        layers = layers.filter((l) => (l as FeatureLayerNFS).isVersioned);
        const adapters = versionManagementAdapterUtils.createVersionAdapters(layers);
        const adaptersCollection: Collection<VersionAdapter> = new Collection();
        adaptersCollection.addMany(adapters);

        const currentVersionIdentifier =
            versionInfos?.find((x) => x.versionIdentifier.name === layers[0].gdbVersion)?.versionIdentifier ??
            versionServiceRef.current.defaultVersionIdentifier;

        const changeResult = await versionServiceRef.current.changeVersionWithResult(
            adaptersCollection,
            currentVersionIdentifier,
            vi.versionIdentifier
        );

        let failureCount = 0;
        let failureMessage = "";
        changeResult.forEach((result) => {
            if (result.error) {
                failureCount++;
                failureMessage = result.error.message;
            }
        });
        if (failureCount > 0) {
            toast.error(
                `${failureCount} av kartlagene kunne ikke endre versjon. Last inn siden og prøv igjen. Mottatt feilmelding er: ${failureMessage}`
            );
            return;
        }

        let guid = vi.versionIdentifier.guid;
        dispatch(setVersionInfo(vi));

        const url = new URL(window.location.href);
        const searchParams = new URLSearchParams(url.searchParams);
        searchParams.set("version", vi.versionIdentifier.name);

        window.history.pushState({}, "", `${window.location.origin}?${searchParams.toString()}`);
        if (!guid) {
            return;
        }
        guid = guid.replace("{", "").replace("}", "");
        dispatch(setActiveGuid(guid));
        if (shouldClosePanel) {
            closePanel();
        }
    };

    const deleteVersion = (versionInfoToDeleteIncoming: VersionInfo) => {
        if (versionInfoToDeleteIncoming.versionIdentifier.guid === versionInfo?.versionIdentifier.guid) {
            if (
                selectedFeature !== null &&
                selectedFeature !== undefined &&
                [
                    RightPanelTabs.EDITOR,
                    RightPanelTabs.LIGHT_CALCULATOR,
                    RightPanelTabs.OVERETT,
                    RightPanelTabs.SEA_CABLE,
                    RightPanelTabs.SECTOR,
                ].includes(rightPanelActiveTab)
            ) {
                // Confirmation popup that informs the user that the selected feature will be closed.
                // If the user confirms, the selected feature will be closed and the version will be changed.
                // If the user cancels, the version will not be changed.
                if (
                    !globalThis.confirm(
                        "Du har et åpent objekt. Hvis du endrer versjon vil dette objektet lukkes. Endringer som ikke er lagret vil gå tapt. Vil du fortsette?"
                    )
                ) {
                    return;
                }
                appContext?.selectedFeature.set(null);
                dispatch(setRightPanelActiveTab(RightPanelTabs.UNDEFINED));
            }
        }

        versionServiceRef.current.getVersionInfos().then((versionInfos) => {
            const versionInfoToDelete = versionInfos.find(
                (x) => x.versionIdentifier.guid === versionInfoToDeleteIncoming.versionIdentifier.guid
            );
            if (!versionInfoToDelete) {
                // alert("Version info not found for this name");
                return;
            }

            versionServiceRef.current.deleteVersion(versionInfoToDelete.versionIdentifier).then(() => {
                reloadVersions();
                deleteMerkeplan(versionInfoToDelete);
            });
            if (versionInfoToDeleteIncoming.versionIdentifier.guid === versionInfo?.versionIdentifier.guid) {
                versionServiceRef.current.load().then(() => {
                    const defaultVersionInfo = versionInfos.find(
                        (x) => x.versionIdentifier.guid === versionServiceRef.current.defaultVersionIdentifier.guid
                    );
                    if (!defaultVersionInfo) {
                        return;
                    }
                    handleSelectVersion(defaultVersionInfo, false, true);
                });
            }
        });
    };

    const openCreateVersion = () => {
        setCreateVersionModalOpen(true);
    };

    const createNewVersion = async (versionName: string, versionDescription: string, isMerkeplan: boolean) => {
        const result = await handleAddVersionClick(versionName, versionDescription, versionServiceRef.current);
        if (result && isMerkeplan) {
            await addMerkeplan(result, userContext?.user.value?.username);
        }
        await reloadVersions();

        return result;
    };

    const canCreateNewVersion = () => {
        const userSelf = userContext?.userSelf.value;
        if (!userSelf) {
            getSelf().then((res: any) => {
                if (res.data) {
                    userContext?.userSelf.set(res.data);
                }
            });
        } else {
            const nautikerGroupId = AppConfig.GroupAccess.Nautiker;
            const canCreateVersion = hasAccessToGroups(userSelf, [nautikerGroupId]);
            setAllowedToCreateVersion(canCreateVersion);
        }
    };

    const selectDefaultVersion = async () => {
        const versionInfos = await versionServiceRef.current.getVersionInfos();
        const defaultVersionInfo = versionInfos.find(
            (x) => x.versionIdentifier.guid === versionServiceRef.current.defaultVersionIdentifier.guid
        );
        if (!defaultVersionInfo) {
            return;
        }
        return await handleSelectVersion(defaultVersionInfo);
    };

    const getDefaultVersion = () => {
        const defaultVersionInfo = versionInfos?.find(
            (x) => x.versionIdentifier.guid === versionServiceRef.current.defaultVersionIdentifier.guid
        );
        if (!defaultVersionInfo) {
            return;
        }
        return defaultVersionInfo;
    };

    const checkForVersionToAccept = () => {
        const url = new URL(window.location.href);
        const searchParams = new URLSearchParams(url.searchParams);

        const versionGuid = searchParams.get("sharing");
        const decodedString = deobscureString(versionGuid ?? "");
        const stringParts = decodedString.split(",");
        const decodedGuid = stringParts[0];
        const expiresAt = stringParts[1];

        if (Date.now() > new Date(parseInt(expiresAt)).getTime()) {
            window.history.pushState({}, "", window.location.origin);
            toast.warning("Delingslenken har utløpt");
            return;
        }

        const versionInfo = versionInfos?.find((x) => x.versionIdentifier.guid === "{" + decodedGuid + "}");
        if (!decodedGuid || !versionInfo) {
            return;
        }

        setVersionToAccept(versionInfo);
        if (versionsSharedWithMe?.includes(versionInfo.versionIdentifier.guid)) {
            setVersionToAccept(null);
            window.history.pushState({}, "", window.location.origin);
            toast.info("Du har allerede akseptert denne versjonen");
            return;
        }

        // Accept version
        setAcceptVersionModalOpen(true);
        window.history.pushState({}, "", window.location.origin);
    };

    const checkForUrlVersion = (): boolean => {
        const url = new URL(window.location.href);
        const searchParams = new URLSearchParams(url.searchParams);

        let versionName = searchParams.get("version");
        if (versionName) {
            versionName = decodeURIComponent(versionName);
        }
        const hasSharing = searchParams.has("sharing");
        if (hasSharing) {
            setTimeout(() => {
                checkForUrlVersion();
            }, 1000);
            return false;
        }
        if (!versionName) {
            selectDefaultVersion();
            return false;
        }

        const tempVersionInfo = versionInfos?.find((x) => {
            const plusSpaceReplaced = versionName.replaceAll("+", " ");
            return plusSpaceReplaced === x.versionIdentifier.name || versionName === x.versionIdentifier.name;
        });
        if (!tempVersionInfo || tempVersionInfo === null) {
            return false;
        }

        if (versionInfo === null || versionInfo?.versionIdentifier.name !== versionName) {
            handleSelectVersion(tempVersionInfo);
            return true;
        }
        return false;
    };

    const validForSearch = (versionInfo: VersionInfoExtended, searchString: string) => {
        if (!searchString) {
            return true;
        }
        if (versionInfo.versionIdentifier.name.toLowerCase().includes(searchString.toLowerCase())) {
            return true;
        }
        if (versionInfo.description?.toLowerCase().includes(searchString.toLowerCase())) {
            return true;
        }
        return false;
    };

    const handleVersionSearchString = (e: React.ChangeEvent<HTMLInputElement>) => {
        setVersionSearchString(e.target.value);
    };

    const filterVersions = (versionInfos?: VersionInfoExtended[], isMe?: boolean) => {
        if (!versionInfos) {
            return [];
        }

        const copiedVersionInfos = [...versionInfos];

        if (versionSort === "A-Å") {
            copiedVersionInfos.sort((a, b) => a.versionIdentifier.name.localeCompare(b.versionIdentifier.name));
        } else if (versionSort === "Å-A") {
            copiedVersionInfos.sort((a, b) => b.versionIdentifier.name.localeCompare(a.versionIdentifier.name));
        } else if (versionSort === "Nyeste") {
            copiedVersionInfos.sort(
                (a, b) => new Date(b.modifiedDate ?? 0).getTime() - new Date(a.modifiedDate ?? 0).getTime()
            );
        } else if (versionSort === "Eldste") {
            copiedVersionInfos.sort(
                (a, b) => new Date(a.modifiedDate ?? 0).getTime() - new Date(b.modifiedDate ?? 0).getTime()
            );
        }

        if (isMe) {
            return copiedVersionInfos.filter((vi) => {
                const user = userContext?.user.value;
                if (!user || user === null) {
                    return false;
                }
                if (!isMyVersion(vi, user)) {
                    return false;
                }
                if (!validForSearch(vi, versionSearchString)) {
                    return false;
                }
                if (isDefaultVersion(vi, versionServiceRef.current)) {
                    return false;
                }
                return true;
            });
        } else {
            return copiedVersionInfos.filter((vi) => {
                if (!validForSearch(vi, versionSearchString)) {
                    return false;
                }
                if (isDefaultVersion(vi, versionServiceRef.current)) {
                    return false;
                }
                return true;
            });
        }
    };

    useEffect(() => {
        const handler = async () => {
            dispatch(setLoading(true));
            await selectDefaultVersion();
            globalThis.dispatchEvent(new CustomEvent(VersionIsDefaultEventType));
            dispatch(setLoading(false));
        };

        globalThis.addEventListener(SwitchToDefaultEventType, handler as EventListener);
        return () => globalThis.removeEventListener(SwitchToDefaultEventType, handler as EventListener);
    }, []);

    useEffect(() => {
        if (versionInfos) {
            checkForVersionToAccept();
            checkForUrlVersion();
        }
    }, [versionInfos]);

    useEffect(() => {
        if (leftPanelActiveTab === LeftPanelTabs.VERSIONS) {
            canCreateNewVersion();
        }
    }, [leftPanelActiveTab, userContext?.userSelf.value]);

    useEffect(() => {
        loadService();
        const tryGetVersions = async () => {
            const user = userContext?.user.value;
            if (user) {
                const versions = await getVersionsSharedWithMe(user);
                dispatch(setVersionsSharedWithMe(versions));
            } else {
                const intervalId = setInterval(async () => {
                    const user = userContext?.user.value;
                    if (user) {
                        clearInterval(intervalId);
                        const versions = await getVersionsSharedWithMe(user);
                        dispatch(setVersionsSharedWithMe(versions));
                    }
                }, 1000); // Retry every second
            }
        };
        const sharedVersionIntervalID = setInterval(() => {
            tryGetVersions();
        }, 1000 * 30);

        return () => {
            clearInterval(sharedVersionIntervalID);
        };
    }, []);

    useEffect(() => {
        if (versionServiceRef.current.loadStatus === "failed") {
            console.log(versionServiceRef.current.loadError);
        } else if (versionServiceRef.current.loadStatus === "loaded") {
            if (!versionInfo) {
                const foundVersion = checkForUrlVersion();
                if (!foundVersion && versionInfos) {
                    handleSelectVersion({
                        versionIdentifier: versionServiceRef.current.defaultVersionIdentifier,
                    } as VersionInfo);
                }
            }
        }
    }, [versionServiceRef.current.loadStatus]);

    return (
        <>
            <div>
                <Modal
                    isOpen={acceptVersionModalOpen}
                    shouldCloseOnOverlayClick={true}
                    shouldCloseOnEsc={true}
                    onRequestClose={() => setAcceptVersionModalOpen(false)}
                    style={{
                        overlay: {
                            background: "rgba(37, 37, 37, 0.50)",
                        },
                        content: {
                            top: "50%",
                            left: "50%",
                            right: "auto",
                            bottom: "auto",
                            marginRight: "-50%",
                            transform: "translate(-50%, -50%)",
                            padding: 0,
                            background: "none",
                            border: "none",
                        },
                    }}
                >
                    <AcceptVersionAccess versionInfo={versionToAccept} close={() => setAcceptVersionModalOpen(false)} />
                </Modal>
            </div>
            <div className="version-control-container">
                <div className="version-control-header">
                    <div className="version-control-header-actions">
                        {allowedToCreateVersion && (
                            <NfsButton onClick={openCreateVersion}>
                                <PlusIcon />
                                <span>Opprett ny versjon</span>
                            </NfsButton>
                        )}
                        <Modal
                            isOpen={createVersionModalOpen}
                            shouldCloseOnOverlayClick={true}
                            shouldCloseOnEsc={true}
                            onRequestClose={() => setCreateVersionModalOpen(false)}
                            style={{
                                overlay: {
                                    background: "rgba(37, 37, 37, 0.50)",
                                },
                                content: {
                                    top: "50%",
                                    left: "50%",
                                    right: "auto",
                                    bottom: "auto",
                                    marginRight: "-50%",
                                    transform: "translate(-50%, -50%)",
                                    padding: 0,
                                    background: "none",
                                    border: "none",
                                },
                            }}
                        >
                            <CreateNewVersion
                                onClose={() => setCreateVersionModalOpen(false)}
                                createNewVersion={createNewVersion}
                                versionInfos={versionInfos ?? []}
                                selectVersion={handleSelectVersion}
                            />
                        </Modal>
                        <button
                            className="empty-button"
                            onClick={() => {
                                reloadVersions();
                            }}
                        >
                            <RefreshIcon width={24} height={24} />
                        </button>
                        <NfsDropdown
                            displayClassName="fit-content no-flex no-border"
                            iconReplacement={<SortIcon />}
                            placeholder="Sorter etter"
                            options={["A-Å", "Å-A", "Nyeste", "Eldste"]}
                            selectedOption={versionSort}
                            setSelectedOption={(option) => {
                                if (option === null) {
                                    setVersionSort("A-Å");
                                } else {
                                    setVersionSort(option as "A-Å" | "Å-A" | "Nyeste" | "Eldste");
                                }
                            }}
                        />
                        <button className="empty-button" onClick={closePanel}>
                            <CloseIcon />
                        </button>
                    </div>
                </div>
                <div className="version-control-content">
                    {isLoading && (
                        <Loader color="#000667" overlay="element" targetElement=".version-control-content" size={60} />
                    )}
                    <Tabs className="h-100 w-100 flex-col" selectedTabClassName="selected">
                        <div className="search-container w-100">
                            <div className="search-input-box w-100">
                                <input
                                    className="search-input"
                                    type="text"
                                    value={versionSearchString}
                                    onChange={handleVersionSearchString}
                                    placeholder="Søk i versjoner"
                                />
                                <MagnifyingGlassIcon />
                            </div>
                        </div>
                        <TabList className="tab-list">
                            <Tab className="tab">
                                <span>{`Opprettet av meg (${filterVersions(versionInfos, true).length})`}</span>
                            </Tab>
                            <Tab className="tab">
                                <span>{`Alle (${filterVersions(versionInfos, false).length})`}</span>
                            </Tab>
                        </TabList>
                        <TabPanel className="tab-panel">
                            <div className="version-control-panel-container">
                                {versionInfos && versionInfos?.length > 0 && (
                                    <VersionPanel
                                        key={getDefaultVersion()?.versionIdentifier.guid}
                                        versionInfo={getDefaultVersion()!}
                                        reloadVersions={reloadVersions}
                                        selectVersion={handleSelectVersion}
                                        deleteVersion={deleteVersion}
                                        versionServiceRef={versionServiceRef}
                                        versionsSharedWithMe={versionsSharedWithMe ?? []}
                                        setIsLoading={setIsLoading}
                                        isMerkeplan={false}
                                    />
                                )}
                                {filterVersions(versionInfos, true).map((vi) => {
                                    return (
                                        <VersionPanel
                                            key={vi.versionIdentifier.guid}
                                            versionInfo={vi}
                                            reloadVersions={reloadVersions}
                                            selectVersion={handleSelectVersion}
                                            deleteVersion={deleteVersion}
                                            versionServiceRef={versionServiceRef}
                                            versionsSharedWithMe={versionsSharedWithMe ?? []}
                                            setIsLoading={setIsLoading}
                                            isMerkeplan={merkeplanList.includes(vi.versionIdentifier.guid)}
                                        />
                                    );
                                })}
                                <div className="no-versions-message">
                                    {versionSearchString === "" && <span>Du har ingen pågående versjoner</span>}
                                    {versionSearchString !== "" && <span>Ingen versjoner funnet</span>}
                                </div>
                            </div>
                        </TabPanel>
                        <TabPanel className="tab-panel">
                            <div className="version-control-panel-container">
                                {versionInfos && versionInfos?.length > 0 && (
                                    <VersionPanel
                                        key={getDefaultVersion()?.versionIdentifier.guid}
                                        versionInfo={getDefaultVersion()!}
                                        reloadVersions={reloadVersions}
                                        selectVersion={handleSelectVersion}
                                        deleteVersion={deleteVersion}
                                        versionServiceRef={versionServiceRef}
                                        versionsSharedWithMe={versionsSharedWithMe ?? []}
                                        setIsLoading={setIsLoading}
                                        isMerkeplan={false}
                                    />
                                )}
                                {filterVersions(versionInfos, false).map((vi) => {
                                    return (
                                        <VersionPanel
                                            key={vi.versionIdentifier.guid}
                                            versionInfo={vi}
                                            reloadVersions={reloadVersions}
                                            selectVersion={handleSelectVersion}
                                            deleteVersion={deleteVersion}
                                            versionServiceRef={versionServiceRef}
                                            versionsSharedWithMe={versionsSharedWithMe ?? []}
                                            setIsLoading={setIsLoading}
                                            isMerkeplan={merkeplanList.includes(vi.versionIdentifier.guid)}
                                        />
                                    );
                                })}
                            </div>
                        </TabPanel>
                    </Tabs>
                </div>
            </div>
        </>
    );
};

export default VersionControl;
